import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { createNumberOptions, hourList } from '@utils/number-arrays';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';

import { getWaitForTimeLength } from '../common';
import { FormGroupContainer } from '../wait-for/journey-sidebar';

type Data = {
  days: string;
  hours: string;
  minutes: string;
};

const ACTION_TYPE = ACTION_TYPES.journey_loop;

const Form = createActionForm<Data>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();

  const minuteList = createNumberOptions(0, 60);
  const days = formStore.useFieldValue<string>('days') ?? '';
  const hours = formStore.useFieldValue<string>('hours') ?? '';
  const minutes = formStore.useFieldValue<string>('minutes') ?? '';
  const timeLengthText = getWaitForTimeLength(days, hours, minutes);

  const values = data?.values;
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const isValid = parseInt(days) > 0 || parseInt(hours) > 0 || parseInt(minutes) >= 10;

  const handleSave = (data: Data) =>
    editMutation.mutateAsync({
      action: ACTION_TYPE,
      isAutomatic: true,
      values: {
        days: { value: data.days },
        hours: { value: data.hours },
        minutes: { value: data.minutes },
      },
      description: null,
    });

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <Text font="regular14" text={t('loopDescription')} />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('loopTimer')}>
              <Stack space={4}>
                <FormGroupContainer>
                  <Form.TextField
                    id="days"
                    label={t('days')}
                    defaultValue={values?.days?.value ?? '0'}
                    validators={['number']}
                  />
                  <Form.Select
                    id="hours"
                    label={t('hours')}
                    options={hourList}
                    placeholder=""
                    defaultValue={values?.hours?.value ?? '12'}
                    isClearable={false}
                  />
                  <Form.Select
                    id="minutes"
                    label={t('minutes')}
                    options={minuteList}
                    placeholder=""
                    defaultValue={values?.minutes?.value ?? '00'}
                    isClearable={false}
                  />
                </FormGroupContainer>
                <Text
                  text={
                    timeLengthText.length > 0 ? t('loopTimerDescription', { timeLengthText }) : ''
                  }
                />
                {!isValid && (
                  <Text font="regular14" color="elementRed100" text={t('loopTimerInvalid')} />
                )}
              </Stack>
            </HideContainer>
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading || !isValid}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    loopTimer: 'Loop timer',
    loopDescription: 'Use when you want to repeat some actions until a condition is met.',
    loopTimerDescription: 'Restart loop every • {timeLengthText}',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    loopTimerInvalid: 'Loop cannot run more frequently than every 10 minutes',
  },
};
